import { storage } from '@/utils/storage'

const numberFormatter = new Intl.NumberFormat()

const defaultConfig = {
  metricsEndpoint: '',
  fallbackUsers: 15432,
  fallbackRequests: 2300,

  // Users/providers: monotonic UTC-based growth
  usersDailyIncrement: 37,
  usersUtcStepSeconds: 1800, // every 30 minutes
  usersStepMinUp: 0,
  usersStepMaxUp: 2,

  // Requests: UTC-based deterministic fluctuation
  requestsUtcStepSeconds: 900, // every 15 minutes
  requestsFluctuationMin: -18,
  requestsFluctuationMax: 24,
  requestsMin: 1800,
  requestsMax: 4000,
}

const isClient = () => typeof window !== 'undefined'

const clampToInt = value => Math.max(0, Math.round(value || 0))
const clamp = (value, min, max) => Math.min(max, Math.max(min, Math.round(value)))

const diffDays = (fromDate, toDate) => {
  const from = new Date(`${fromDate}T00:00:00Z`)
  const to = new Date(`${toDate}T00:00:00Z`)

  return Math.max(0, Math.floor((to - from) / 86400000))
}

function getTodayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

function getUtcEpochSeconds() {
  return Math.floor(Date.now() / 1000)
}

/**
 * Small deterministic pseudo-random number based on integer seed.
 * Returns a float in [0, 1).
 */
function seededUnitRandom(seed) {
  const x = Math.sin(seed * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

function seededInt(seed, min, max) {
  const random = seededUnitRandom(seed)
  return Math.floor(random * (max - min + 1)) + min
}

function getUsersDailyBase(config) {
  if (!isClient()) {
    return config.fallbackUsers
  }

  try {
    const storageKey = 'metric_users_daily_base'
    const today = getTodayIsoDate()
    const storedValue = JSON.parse(storage.getItem(storageKey) || '{}')

    const seed = typeof storedValue.seed === 'number'
      ? storedValue.seed
      : config.fallbackUsers

    const lastDay = storedValue.lastDay || today
    const previousOffset = typeof storedValue.offset === 'number'
      ? storedValue.offset
      : 0

    const elapsedDays = diffDays(lastDay, today)
    const offset = previousOffset + (elapsedDays * config.usersDailyIncrement)

    storage.setItem(storageKey, JSON.stringify({
      seed,
      lastDay: today,
      offset,
    }))

    return seed + offset
  } catch (_) {
    return config.fallbackUsers
  }
}

function getUtcDriftedUsers(baseUsers, config) {
  const epochSeconds = getUtcEpochSeconds()
  const stepIndex = Math.floor(epochSeconds / config.usersUtcStepSeconds)

  // Small deterministic upward increments across UTC steps
  let extra = 0

  // Use a limited lookback window to avoid unbounded accumulation speed.
  // We combine the current day baseline with today's UTC step drift.
  const todayStartUtc = Math.floor(new Date(`${getTodayIsoDate()}T00:00:00Z`).getTime() / 1000)
  const todayStepStart = Math.floor(todayStartUtc / config.usersUtcStepSeconds)
  const stepsSinceTodayStart = Math.max(0, stepIndex - todayStepStart)

  for (let i = 0; i < stepsSinceTodayStart; i += 1) {
    extra += seededInt(
      todayStepStart + i,
      config.usersStepMinUp,
      config.usersStepMaxUp
    )
  }

  return clampToInt(baseUsers + extra)
}

function getUtcDriftedRequests(baseRequests, config) {
  const epochSeconds = getUtcEpochSeconds()
  const stepIndex = Math.floor(epochSeconds / config.requestsUtcStepSeconds)

  const delta = seededInt(
    stepIndex,
    config.requestsFluctuationMin,
    config.requestsFluctuationMax
  )

  return clamp(
    baseRequests + delta,
    config.requestsMin,
    config.requestsMax
  )
}

async function fetchMetricsSafe(endpoint) {
  if (!endpoint) {
    return {}
  }

  try {
    const response = await fetch(endpoint, { credentials: 'omit' })

    if (!response.ok) {
      throw new Error(`status ${response.status}`)
    }

    const json = await response.json()

    return {
      users: typeof json.users === 'number' ? json.users : undefined,
      requests: typeof json.requests === 'number' ? json.requests : undefined,

      // Backward compatibility
      jobsCompleted: typeof json.jobsCompleted === 'number' ? json.jobsCompleted : undefined,
    }
  } catch (_) {
    return {}
  }
}

export default {
  namespaced: true,

  state: () => ({
    users: 0,
    requests: 0,
    config: { ...defaultConfig },
    isLoaded: false,
  }),

  getters: {
    usersFormatted: state => numberFormatter.format(clampToInt(state.users)),
    requestsFormatted: state => numberFormatter.format(clampToInt(state.requests)),
  },

  mutations: {
    SET_CONFIG(state, config) {
      state.config = { ...state.config, ...config }
    },
    SET_USERS(state, value) {
      state.users = clampToInt(value)
    },
    SET_REQUESTS(state, value) {
      state.requests = clampToInt(value)
    },
    SET_LOADED(state, value) {
      state.isLoaded = !!value
    },
  },

  actions: {
    async init({ state, commit }, customConfig = {}) {
      commit('SET_CONFIG', customConfig)

      const config = state.config

      const fetchedMetrics = await fetchMetricsSafe(config.metricsEndpoint)

      const usersBase = fetchedMetrics.users != null
        ? fetchedMetrics.users
        : getUsersDailyBase(config)

      const requestsBase = fetchedMetrics.requests != null
        ? fetchedMetrics.requests
        : fetchedMetrics.jobsCompleted != null
          ? fetchedMetrics.jobsCompleted
          : config.fallbackRequests

      const users = getUtcDriftedUsers(usersBase, config)
      const requests = getUtcDriftedRequests(requestsBase, config)

      commit('SET_USERS', users)
      commit('SET_REQUESTS', requests)
      commit('SET_LOADED', true)
    },

    async reconfigure({ dispatch, commit }, customConfig = {}) {
      commit('SET_CONFIG', customConfig)
      await dispatch('init')
    },
  },
}